/**
 * FIFINE MX - Upsell al agregar al carrito
 * ---------------------------------------------------------------------------
 * Escucha `fifine:cart:beforeopen` -que emite fifine-cart.js de forma
 * cancelable- y, si alguna regla aplica, cancela la apertura del cajon y
 * muestra la oferta. Cuando el cliente decide, se abre el cajon.
 *
 * Sin esa coordinacion se apilarian dos capas encima del contenido.
 *
 * Una regla no se muestra si:
 *   - el producto ofrecido ya esta en el carrito
 *   - el cliente ya la descarto en esta sesion (sessionStorage, no localStorage:
 *     al abrir una sesion nueva la oferta vuelve a ser relevante)
 */

const KEY = 'fifine:upsell:dismissed';

const root = document.getElementById('fif-upsell');
if (root) {
  const panel = root.querySelector('.fif-upsell__panel');
  const offers = [...root.querySelectorAll('[data-fif-upsell-rule]')];

  let lastFocus = null;

  const dismissed = () => {
    try {
      return new Set(JSON.parse(sessionStorage.getItem(KEY) || '[]'));
    } catch {
      return new Set();
    }
  };

  const remember = (id) => {
    try {
      const set = dismissed();
      set.add(id);
      sessionStorage.setItem(KEY, JSON.stringify([...set]));
    } catch {
      /* modo privado: no se recuerda, la oferta puede repetirse */
    }
  };

  const focusables = () =>
    [...panel.querySelectorAll('a[href], button:not([disabled]), [tabindex]:not([tabindex="-1"])')].filter(
      (el) => el.offsetParent !== null
    );

  function show(offer) {
    lastFocus = document.activeElement;
    for (const el of offers) el.hidden = el !== offer;
    root.hidden = false;
    void panel.offsetHeight; // reflow para que la transicion arranque
    root.classList.add('is-open');
    panel.setAttribute('aria-hidden', 'false');
    document.body.classList.add('fif-cart-open');
    focusables()[0]?.focus();
  }

  function hide({ thenOpenCart = false } = {}) {
    if (!root.classList.contains('is-open')) return;
    root.classList.remove('is-open');
    panel.setAttribute('aria-hidden', 'true');
    document.body.classList.remove('fif-cart-open');

    const done = () => {
      root.hidden = true;
      panel.removeEventListener('transitionend', done);
      if (thenOpenCart) window.fifineCart?.open?.();
    };
    panel.addEventListener('transitionend', done);
    lastFocus?.focus?.();
  }

  /**
   * @param {number[]} addedIds Productos recien agregados.
   * @param {object} cart Estado actual del carrito.
   * @returns {Element|null}
   */
  function match(addedIds, cart) {
    const inCart = new Set(cart.items.map((i) => i.product_id));
    const skip = dismissed();

    for (const el of offers) {
      const offerId = Number(el.dataset.offerId);
      if (inCart.has(offerId) || skip.has(offerId)) continue;

      const triggers = (el.dataset.triggers || '')
        .split(',')
        .map((n) => Number(n.trim()))
        .filter(Boolean);

      const disparo = addedIds.find((id) => triggers.includes(id));
      if (disparo !== undefined) {
        // Se guarda la LINEA del carrito que disparo la regla: si la oferta
        // reemplaza, hay que poder quitarla despues por su key.
        const linea = cart.items.find((i) => i.product_id === disparo);
        el.__triggerKey = linea ? linea.key : null;
        return el;
      }
    }
    return null;
  }

  document.addEventListener('fifine:cart:beforeopen', (e) => {
    const { productIds = [], cart } = e.detail || {};
    if (!cart || !productIds.length) return;

    const offer = match(productIds, cart);
    if (!offer) return; // sin regla: que el cajon se abra normal

    e.preventDefault();
    show(offer);
  });

  /* Si el cliente acepta la oferta, el alta dispara el evento del carrito:
     se cierra la ventana y se abre el cajon con todo dentro. */
  /* Si la regla es un CAMBIO y no un agregado, el formulario de la oferta solo
     sabe agregar: aqui se quita la linea que disparo la regla. Sin esto, el
     boton dice "Cambiar" y deja los dos productos en el carrito. */
  document.addEventListener('fifine:cart:added', async () => {
    if (!root.classList.contains('is-open')) return;

    const visible = offers.find((el) => !el.hidden);
    if (visible && visible.dataset.replace === 'true' && visible.__triggerKey) {
      try {
        await fetch('/cart/change.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ id: visible.__triggerKey, quantity: 0 }),
        });
      } catch (err) {
        /* Si falla, se queda el agregado: peor es dejar el carrito a medias. */
      }
    }

    hide({ thenOpenCart: true });
  });

  root.addEventListener('click', (e) => {
    if (!e.target.closest('[data-fif-upsell-dismiss]')) return;
    const visible = offers.find((el) => !el.hidden);
    if (visible) remember(Number(visible.dataset.offerId));
    hide({ thenOpenCart: true });
  });

  document.addEventListener('keydown', (e) => {
    if (!root.classList.contains('is-open')) return;

    if (e.key === 'Escape') {
      const visible = offers.find((el) => !el.hidden);
      if (visible) remember(Number(visible.dataset.offerId));
      hide({ thenOpenCart: true });
      return;
    }

    if (e.key === 'Tab') {
      const list = focusables();
      if (!list.length) return;
      const first = list[0];
      const last = list[list.length - 1];
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    }
  });
}
